
import React from 'react';
import { CASE_STUDIES } from '../constants';
import { CaseStudy } from '../types';
import SmartImage from './SmartImage';

interface PortfolioGridProps {
  onOpenDetail: (study: CaseStudy) => void;
}

const PortfolioCard: React.FC<{ study: CaseStudy; index: number; onClick: () => void }> = ({ study, index, onClick }) => {
  return (
    <div 
      onClick={onClick}
      className="group relative h-[60vh] md:h-[70vh] w-full overflow-hidden cursor-pointer border border-white/5 bg-zinc-900"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <SmartImage 
          src={study.imageUrl} 
          alt={study.title} 
          className="w-full h-full object-cover grayscale brightness-50 scale-100 group-hover:scale-110 group-hover:grayscale-0 group-hover:brightness-75 transition-all duration-1000 ease-out"
        />
      </div>

      {/* Coming Soon Banner - Upgraded Scale */}
      {study.comingSoon && (
        <div className="absolute top-0 right-0 z-30 overflow-hidden w-64 h-64 pointer-events-none">
          <div className="bg-primary text-white text-base font-black uppercase tracking-[0.3em] py-3 w-96 text-center absolute top-14 -right-24 rotate-45 shadow-2xl border-y border-white/20">
            Coming Soon
          </div>
        </div>
      )}

      {/* Overlay Gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>

      {/* Content */}
      <div className="absolute inset-0 z-10 p-8 flex flex-col justify-end">
        <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-700">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-primary font-black uppercase tracking-[0.4em] text-[10px] block">
              0{index + 1} / {study.comingSoon ? 'Future' : 'Project'}
            </span>
          </div>
          <h3 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white leading-none mb-3">
            {study.title}
          </h3>
          <p className="text-white/40 text-sm font-bold uppercase tracking-widest overflow-hidden h-0 group-hover:h-6 transition-all duration-500">
            {study.category}
          </p>
        </div>
      </div>

      {/* Hover Line */}
      <div className={`absolute bottom-0 left-0 h-1 w-0 ${study.comingSoon ? 'bg-white/20' : 'bg-primary'} group-hover:w-full transition-all duration-700`}></div>
    </div>
  );
};

const PortfolioGrid: React.FC<PortfolioGridProps> = ({ onOpenDetail }) => {
  return (
    <section className="h-full w-full bg-background-dark overflow-y-auto overscroll-contain px-6 lg:px-12 py-32">
      <div className="max-w-[1600px] mx-auto pb-12">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
          <div>
            <h2 className="text-6xl md:text-8xl font-black uppercase tracking-tighter italic leading-none text-white">
              App <br/>
              <span className="text-stroke">Portfolio</span>
            </h2>
          </div>
          <div className="max-w-xs text-right">
            <p className="text-white/40 text-xs font-bold uppercase tracking-widest leading-loose">
              A collection of digital products engineered for impact and designed for the soul.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
          {CASE_STUDIES.map((study, idx) => (
            <PortfolioCard 
              key={study.id} 
              study={study} 
              index={idx} 
              onClick={() => onOpenDetail(study)} 
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioGrid;
